import {
  AutoAdvanceConfig,
  BannerButton,
  BannerVariant,
  ModalConfig,
  OnAppEventConfig,
  OnMediaCloseConfig,
  PathGated,
  SpotlightConfig,
  TourContext,
  TourStep,
  Varied,
  VisibleBannerVariant,
} from '../types';
import { evaluateCond } from './conditions';
import { interpolate } from './interpolate';
import { matchesPath } from './route';

const matchesWhen = (variant: PathGated, pathname: string): boolean => {
  if (!variant.when) return true; // no gate -> catch-all
  const patterns = Array.isArray(variant.when) ? variant.when : [variant.when];
  return patterns.some((pattern) => matchesPath(pattern, pathname));
};

/**
 * Pick the first variant whose gates all pass (`when` and `cond` AND together). A variant with
 * neither gate is a catch-all, so a single (non-array) config always wins — which keeps every
 * existing step working unchanged.
 *
 * `pending` stops resolution instead of falling through. Falling through would render the
 * catch-all copy and then swap it once the data arrived; stopping renders nothing until the
 * answer is real.
 */
export const pickVariant = <T extends PathGated>(
  config: Varied<T> | undefined,
  pathname: string,
  ctx?: TourContext,
): T | undefined => {
  if (!config) return undefined;
  const variants = Array.isArray(config) ? config : [config];

  for (let i = 0; i < variants.length; i += 1) {
    const variant = variants[i];
    if (matchesWhen(variant, pathname)) {
      const cond = evaluateCond(variant.cond, ctx);
      if (cond === 'pending') return undefined;
      // A hidden variant still consumes the match — it suppresses the component rather than
      // falling through to a later catch-all.
      if (cond) return variant.hidden ? undefined : variant;
    }
  }
  return undefined;
};

/**
 * Every entry whose gates pass, not just the first.
 *
 * `pickVariant` answers "which ONE of these applies", which is right for anything that occupies a
 * slot — there is one banner, one modal. Listeners are not exclusive: a step can wait on several
 * actions at once and send each somewhere different ("zoom in" and "switch camera" are both valid
 * answers to the same prompt). A `pending` entry is skipped individually rather than suppressing
 * the rest, since one unresolved condition should not deafen the step to every other action.
 */
export const pickAll = <T extends PathGated>(config: Varied<T> | undefined, pathname: string, ctx?: TourContext): T[] => {
  if (!config) return [];
  const variants = Array.isArray(config) ? config : [config];
  return variants.filter(
    (variant) => !variant.hidden && matchesWhen(variant, pathname) && evaluateCond(variant.cond, ctx) === true,
  );
};

/**
 * Fill a button's copy and destination. Undefined if any token is unresolved, so an unfilled
 * `{{ctx.recommendedHabitat.slug}}` can never ship a broken link.
 */
const resolveButton = (button: BannerButton, ctx: TourContext | undefined): BannerButton | undefined => {
  const label = interpolate(button.label, ctx);
  const navigatePath = interpolate(button.navigatePath, ctx);
  const devNavigatePath = interpolate(button.devNavigatePath, ctx);

  if (label === undefined) return undefined;
  if (button.navigatePath && navigatePath === undefined) return undefined;
  if (button.devNavigatePath && devNavigatePath === undefined) return undefined;

  return { ...button, label, navigatePath, devNavigatePath };
};

const resolveButtons = (
  buttons: BannerButton[] | undefined,
  ctx: TourContext | undefined,
): BannerButton[] | undefined | null => {
  if (!buttons) return buttons;

  const resolved: BannerButton[] = [];
  for (let i = 0; i < buttons.length; i += 1) {
    const cond = evaluateCond(buttons[i].cond, ctx);
    // null distinguishes "cannot resolve yet" from "there were no buttons": a half-built button
    // row would offer the user a choice that is about to change under them.
    if (cond === 'pending') return null;
    if (cond) {
      const button = resolveButton(buttons[i], ctx);
      if (!button) return null;
      resolved.push(button);
    }
  }
  return resolved;
};

/**
 * Fill a banner's tokens. Any unresolved token collapses the whole banner, the same outcome as
 * a `pending` condition — a half-filled banner ("We are LIVE from .") is exactly the flicker
 * the tri-state exists to prevent.
 */
const resolveBannerCopy = (
  banner: VisibleBannerVariant | undefined,
  ctx: TourContext | undefined,
): VisibleBannerVariant | undefined => {
  if (!banner) return undefined;

  if (banner.type === 'offer-checkout') {
    // No button row to resolve — its single action is the checkout the component runs. Only the
    // speech bubble carries tokens (the recommended animal's name).
    const speech = interpolate(banner.speechBubble, ctx);
    if (banner.speechBubble && speech === undefined) return undefined;
    return { ...banner, speechBubble: speech };
  }

  const buttons = resolveButtons(banner.buttons, ctx);
  if (buttons === null) return undefined;

  if (banner.type === 'paused') {
    const currentSection = interpolate(banner.currentSection, ctx);
    const nextHabitat = interpolate(banner.nextHabitat, ctx);
    if (currentSection === undefined) return undefined;
    if (banner.nextHabitat && nextHabitat === undefined) return undefined;
    return { ...banner, currentSection, nextHabitat, buttons };
  }

  const speechBubble = interpolate(banner.speechBubble, ctx);
  if (banner.speechBubble && speechBubble === undefined) return undefined;

  const backButton = banner.backButton && resolveButton(banner.backButton, ctx);
  const breakButton = banner.breakButton && resolveButton(banner.breakButton, ctx);
  if (banner.backButton && !backButton) return undefined;
  if (banner.breakButton && !breakButton) return undefined;

  return { ...banner, speechBubble, buttons, backButton: backButton || undefined, breakButton: breakButton || undefined };
};

export interface ResolvedUi {
  banner?: VisibleBannerVariant;
  autoAdvance?: AutoAdvanceConfig;
  onMediaClose?: OnMediaCloseConfig;
  modal?: ModalConfig;
  spotlight?: SpotlightConfig;
  /** Every matching listener, not one: a step may wait on several actions at once. */
  onAppEvent: OnAppEventConfig[];
}

/** Resolve every component's config for this step at the current path and context. */
export const resolveUi = (step: TourStep | undefined, pathname: string, ctx?: TourContext): ResolvedUi => {
  if (!step) return { onAppEvent: [] };
  const banner = pickVariant<BannerVariant>(step.ui.banner, pathname, ctx) as VisibleBannerVariant | undefined;

  return {
    banner: resolveBannerCopy(banner, ctx),
    autoAdvance: pickVariant<AutoAdvanceConfig>(step.ui.autoAdvance, pathname, ctx),
    onMediaClose: pickVariant<OnMediaCloseConfig>(step.ui.onMediaClose, pathname, ctx),
    modal: pickVariant<ModalConfig>(step.ui.modal, pathname, ctx),
    spotlight: pickVariant<SpotlightConfig>(step.ui.spotlight, pathname, ctx),
    onAppEvent: pickAll<OnAppEventConfig>(step.ui.onAppEvent, pathname, ctx),
  };
};
